import React, { useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  LinearProgress,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  AutoAwesome,
  ExpandMore,
  FileDownload,
  PictureAsPdf,
} from "@mui/icons-material";
import { useToast } from "@/shared/toast/useToast";
import type {
  ComplianceResult,
  ComplianceRun,
} from "@/features/stationAdmin/api/iaComplianceApi";
import {
  SEVERITY_CHIP_COLOR,
  SEVERITY_LABEL,
  completedRunOutcome,
  normalizeFindings,
  resultVerdictChip,
  runStatusChip,
  type NormalizedFinding,
} from "./complianceView";
import { exportComplianceRunPdf } from "./complianceReportHtml";
import { exportComplianceRunCsv } from "./complianceReportCsv";

interface ComplianceResultsListProps {
  /** The selected run; undefined while its first detail fetch is in flight. */
  run: ComplianceRun | undefined;
  results: ComplianceResult[] | undefined;
  isLoading: boolean;
  isError: boolean;
  /** Refetches the run detail (the poll stops on errors — this restarts it). */
  onRetry: () => void;
  /** Station name included in exported artifacts (optional). */
  stationName?: string;
}

function FindingItem({ finding }: { finding: NormalizedFinding }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        py: 1.25,
        px: 1.5,
        borderRadius: 1.5,
        border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Chip
          size="small"
          color={SEVERITY_CHIP_COLOR[finding.severity]}
          label={SEVERITY_LABEL[finding.severity]}
          sx={{ height: 20, fontSize: "0.7rem", fontWeight: 600 }}
        />
        {finding.policyReference && (
          <Typography variant="caption" color="text.secondary">
            {finding.policyReference}
          </Typography>
        )}
      </Stack>
      <Typography variant="body2" sx={{ mt: 0.75, fontWeight: 500 }}>
        {finding.requirement}
      </Typography>
      {finding.evidence && (
        <Box
          sx={{
            mt: 0.75,
            pl: 1.5,
            borderLeft: `3px solid ${alpha(theme.palette.divider, 0.8)}`,
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontStyle: "italic", whiteSpace: "pre-wrap" }}
          >
            {finding.evidence}
          </Typography>
        </Box>
      )}
      {finding.explanation && (
        <Typography variant="body2" sx={{ mt: 0.75 }}>
          {finding.explanation}
        </Typography>
      )}
    </Box>
  );
}

function ResultItem({ result }: { result: ComplianceResult }) {
  // Defensive normalization even though the backend sanitizes — malformed
  // model output must degrade to fewer findings, never crash the list.
  const findings = useMemo(
    () => normalizeFindings(result.findings),
    [result.findings],
  );
  const verdict = resultVerdictChip(result);
  const title = result.title || `Report ${result.reportId}`;

  return (
    <Accordion disableGutters variant="outlined" sx={{ borderRadius: 2, "&:before": { display: "none" } }}>
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{ "& .MuiAccordionSummary-content": { minWidth: 0 } }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ flex: 1, minWidth: 0, pr: 1 }}
        >
          <Typography variant="body2" fontWeight={600} noWrap sx={{ flex: 1 }}>
            {title}
          </Typography>
          {result.confidence && (
            <Typography variant="caption" color="text.secondary">
              {result.confidence} confidence
            </Typography>
          )}
          {findings.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              {findings.length}{" "}
              {findings.length === 1 ? "finding" : "findings"}
            </Typography>
          )}
          <Chip
            size="small"
            color={verdict.color}
            label={verdict.label}
            sx={{ height: 20, fontSize: "0.7rem", fontWeight: 600 }}
          />
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        {result.status === "error" ? (
          <Alert severity="warning">
            {result.error || "This report couldn't be checked."}
          </Alert>
        ) : (
          <Stack spacing={1.5}>
            {result.summary && (
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {result.summary}
              </Typography>
            )}
            {findings.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No findings for this report.
              </Typography>
            ) : (
              findings.map((finding) => (
                <FindingItem key={finding.id} finding={finding} />
              ))
            )}
          </Stack>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

/**
 * Per-report results for the selected compliance run, with the run's live
 * status area as a polite live region so screen readers hear progress and
 * completion (AiSummaryCard idiom).
 */
export const ComplianceResultsList: React.FC<ComplianceResultsListProps> = ({
  run,
  results,
  isLoading,
  isError,
  onRetry,
  stationName,
}) => {
  const theme = useTheme();
  const toast = useToast();
  const [exportingPdf, setExportingPdf] = useState(false);
  const chip = run ? runStatusChip(run.status) : null;
  const inProgress = run?.status === "queued" || run?.status === "running";
  const completedOutcome =
    run?.status === "completed" ? completedRunOutcome(run) : null;

  // Export only a settled run that actually has results — an in-flight or
  // empty run would produce a misleading partial artifact for an IA case.
  const canExport =
    !!run && !inProgress && !!results && results.length > 0 && !exportingPdf;

  const handlePdfExport = async () => {
    if (!run || !results) return;
    setExportingPdf(true);
    try {
      await exportComplianceRunPdf({ run, results, stationName });
    } catch {
      // exportComplianceRunPdf logs the underlying error.
      toast.error("Couldn't generate the PDF. Please try again.");
    } finally {
      setExportingPdf(false);
    }
  };

  const handleCsvExport = () => {
    if (!run || !results) return;
    try {
      exportComplianceRunCsv({ run, results, stationName });
    } catch {
      toast.error("Couldn't generate the CSV. Please try again.");
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        borderColor: alpha(theme.palette.primary.main, 0.3),
      }}
    >
      <CardContent>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
            mb: 1.5,
          }}
        >
          <Typography variant="subtitle1" fontWeight={700}>
            Check Results
          </Typography>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="flex-end"
            useFlexGap
            sx={{ flexWrap: "wrap", minWidth: 0, maxWidth: "100%" }}
          >
            <Tooltip title="Export these findings as a PDF to attach to an IA case">
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={
                    exportingPdf ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <PictureAsPdf fontSize="small" />
                    )
                  }
                  onClick={() => void handlePdfExport()}
                  disabled={!canExport}
                >
                  Export PDF
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Export these findings as a CSV spreadsheet">
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<FileDownload fontSize="small" />}
                  onClick={handleCsvExport}
                  disabled={!canExport}
                >
                  Export CSV
                </Button>
              </span>
            </Tooltip>
            <Chip
              size="small"
              icon={<AutoAwesome sx={{ fontSize: 14 }} />}
              label="AI-generated"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: "primary.main",
                fontWeight: 600,
              }}
            />
          </Stack>
        </Box>

        {/* role="status" announces run progress/completion politely. */}
        <Box role="status" sx={{ mb: 2 }}>
          {run && chip && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                size="small"
                color={chip.color}
                label={chip.label}
                icon={
                  chip.inProgress ? (
                    <CircularProgress size={12} color="inherit" />
                  ) : undefined
                }
                sx={{ height: 22, fontWeight: 600 }}
              />
              <Typography variant="body2" color="text.secondary">
                {inProgress
                  ? `Checked ${run.checkedCount} of ${run.reportCount} reports…`
                  : completedOutcome?.detail ||
                    run.error ||
                    "The compliance check failed."}
              </Typography>
            </Stack>
          )}
          {inProgress && run && (
            <LinearProgress
              variant="determinate"
              value={
                run.reportCount > 0
                  ? (run.checkedCount / run.reportCount) * 100
                  : 0
              }
              sx={{ mt: 1, borderRadius: 1 }}
            />
          )}
        </Box>

        {isError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={onRetry}>
                Retry
              </Button>
            }
          >
            Couldn&apos;t load the check results right now.
            {results && results.length > 0
              ? " Showing the last loaded results."
              : ""}
          </Alert>
        )}
        {isError && (!results || results.length === 0) ? null : isLoading &&
          !results ? (
          <Stack spacing={1}>
            <Skeleton variant="rounded" height={48} sx={{ borderRadius: 2 }} />
            <Skeleton variant="rounded" height={48} sx={{ borderRadius: 2 }} />
            <Skeleton variant="rounded" height={48} sx={{ borderRadius: 2 }} />
          </Stack>
        ) : !results || results.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontStyle: "italic" }}
          >
            {inProgress
              ? "Results will appear here as each report is checked."
              : "No results for this check."}
          </Typography>
        ) : (
          <Stack spacing={1}>
            {results.map((result) => (
              <ResultItem key={result.reportId} result={result} />
            ))}
          </Stack>
        )}

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 2 }}
        >
          AI checks are advisory — verify findings against the policy text
          before acting on them.
        </Typography>
      </CardContent>
    </Card>
  );
};
